import { useTranslation } from 'react-i18next'
import type { VerifyLoadingStepDef } from '../../utils/verifyLoadingConfig'
import type { VerifyLoadingStepVisual } from '../../hooks/useVerifyLoadingProgress'
import VerifyProgressStep from './VerifyProgressStep'
import { IconLoaderCircle } from '../verify-report/VerifyReportIcons'

type Props = {
  steps: VerifyLoadingStepDef[]
  stepVisual: (index: number) => VerifyLoadingStepVisual
  statusStepIndex: number
  readyMessage: boolean
  failed: boolean
  errorMessage?: string | null
  onRetry?: () => void
  savedToProfile?: boolean
  previewDemo?: boolean
  onContinueToReport?: () => void
}

function trackFillPercent(stepVisual: (index: number) => VerifyLoadingStepVisual, count: number): number {
  if (count <= 1) return 0
  let fillIndex = 0
  for (let i = 0; i < count; i++) {
    const visual = stepVisual(i)
    if (visual === 'done') fillIndex = i
    if (visual === 'active') {
      fillIndex = i
      break
    }
    if (visual === 'error') {
      fillIndex = i
      break
    }
  }
  return (fillIndex / (count - 1)) * 100
}

export default function VerifyProgress({
  steps,
  stepVisual,
  statusStepIndex,
  readyMessage,
  failed,
  errorMessage,
  onRetry,
  savedToProfile = false,
  previewDemo = false,
  onContinueToReport,
}: Props) {
  const { t } = useTranslation()
  const statusStep = steps[statusStepIndex]
  const statusText = failed
    ? t('vc.verifyLoading.error.title')
    : readyMessage
      ? t('vc.verifyLoading.ready.title')
      : statusStep
        ? t(statusStep.pendingKey)
        : ''

  const trackFill = trackFillPercent(stepVisual, steps.length)

  return (
    <div className={`verify-loading-progress${readyMessage ? ' verify-loading-progress--ready' : ''}`}>
      <div
        className="verify-loading-progress__stepper"
        style={{ ['--verify-step-count' as string]: steps.length }}
      >
        <div className="verify-loading-progress__track" aria-hidden="true">
          <div className="verify-loading-progress__track-rail" />
          <div
            className="verify-loading-progress__track-fill"
            style={{ width: `${trackFill}%` }}
          />
        </div>
        <ol className="verify-loading-progress__steps">
          {steps.map((step, index) => (
            <VerifyProgressStep key={step.id} label={t(step.labelKey)} visual={stepVisual(index)} />
          ))}
        </ol>
      </div>

      {failed ? (
        <div className="verify-loading-progress__error" role="alert">
          <p className="verify-loading-progress__error-title">{statusText}</p>
          {errorMessage ? (
            <p className="verify-loading-progress__error-copy">{errorMessage}</p>
          ) : (
            <p className="verify-loading-progress__error-copy">{t('vc.verifyLoading.error.copy')}</p>
          )}
          {onRetry ? (
            <button type="button" className="verify-loading-progress__retry" onClick={onRetry}>
              {t('vc.verifyLoading.error.retry')}
            </button>
          ) : null}
        </div>
      ) : readyMessage ? (
        <div className="verify-loading-progress__ready-panel" role="status" aria-live="polite">
          <div className="verify-loading-progress__ready-detail">
            {previewDemo ? (
              <p>{t('vc.verifyLoading.ready.demo')}</p>
            ) : savedToProfile ? (
              <p>{t('vc.verifyLoading.ready.profile')}</p>
            ) : (
              <p>{t('vc.verifyLoading.ready.guest')}</p>
            )}
          </div>
          {onContinueToReport && !previewDemo ? (
            <button
              type="button"
              className="verify-loading-progress__continue verify-loading-progress__continue--primary"
              onClick={onContinueToReport}
            >
              {t('vc.verifyLoading.ready.cta')}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="verify-loading-progress__status" role="status" aria-live="polite">
          <IconLoaderCircle className="verify-loading-progress__status-spinner" />
          <div className="verify-loading-progress__status-copy">
            <p className="verify-loading-progress__status-line">{statusText}</p>
            <p className="verify-loading-progress__status-hint">{t('vc.verifyLoading.hint')}</p>
          </div>
        </div>
      )}
    </div>
  )
}
